import { type ChangeEvent, type ReactNode, useEffect } from "react";

import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { SearchInput } from "../../../Input/SearchInput/SearchInput";
import { type NestedOptionType } from "../types";

import { SubOption } from "./SubOption";

type SubOptionsPanelProps = {
  parentOption: NestedOptionType;
  showSearch?: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  filteredSubOptions: NestedOptionType[] | undefined;
  isOptionSelected: (optionValue: string) => boolean;
  onSelectOption: (option: NestedOptionType) => void;
  renderSubOptionLabel?: (
    option: NestedOptionType,
    parentValue: string,
  ) => ReactNode;
  selectedParent: string | null;
  setSelectedParent: (value: string | null) => void;
};

export const SubOptionsPanel = ({
  parentOption,
  showSearch,
  searchValue,
  setSearchValue,
  filteredSubOptions,
  isOptionSelected,
  onSelectOption,
  renderSubOptionLabel,
  setSelectedParent,
}: SubOptionsPanelProps) => {
  const hasFilteredResults =
    filteredSubOptions && filteredSubOptions.length > 0;

  // Selects option only when is selectable
  useEffect(() => {
    if (parentOption.value && parentOption.subOptions) {
      setSelectedParent(parentOption.value);
    }
  }, [parentOption.value, parentOption.subOptions, setSelectedParent]);

  return (
    <div
      className="w-72 bg-background/card rounded-companyBorderRadius border border-border/secondary shadow-md z-50"
      style={{
        marginTop: "-1px",
        maxHeight: "16rem",
        overflowY: "auto",
      }}
    >
      {showSearch ? (
        <SearchInput
          className="p-2"
          name="sub-options-search"
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setSearchValue(event.currentTarget.value);
          }}
          value={searchValue}
          withBorder
        />
      ) : null}

      <div className={cnTwMerge("px-1 pb-2", !showSearch && "py-3")}>
        {/* Show "No results found" when search returns empty */}
        {parentOption.subOptions && searchValue && !hasFilteredResults ? (
          <div className="px-6 py-4 text-text/primary text-center body-base-medium">
            {m.common_noResultsFound()}
          </div>
        ) : (
          filteredSubOptions?.map((subOption, subIndex) => (
            <SubOption
              index={subIndex}
              isSelected={isOptionSelected(subOption.value)}
              key={subOption.value}
              onSelect={() => onSelectOption(subOption)}
              option={subOption}
              renderLabel={
                renderSubOptionLabel
                  ? () => renderSubOptionLabel(subOption, parentOption.value)
                  : subOption.renderLabel
              }
              totalOptions={filteredSubOptions.length}
            />
          ))
        )}
      </div>
    </div>
  );
};
